import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import QuratorLayout from '../components/qurator/QuratorLayout';
import API from '../config/api';
import './PlaySimulation.css';

function getTimelineActiveStepId(currentTime, timeline, actionSteps) {
  if (!Array.isArray(timeline) || timeline.length === 0) {
    return actionSteps[0]?.id ?? null;
  }

  const sorted = [...timeline].sort(
    (a, b) => Number(a?.approx_start_s) - Number(b?.approx_start_s)
  );

  for (const entry of sorted) {
    const start = Number(entry?.approx_start_s);
    const end = Number(entry?.approx_end_s);
    if (!Number.isFinite(start)) continue;
    if (Number.isFinite(end) && currentTime >= start && currentTime <= end) {
      return entry.step_id;
    }
  }

  let lastStartedId = null;
  for (const entry of sorted) {
    const start = Number(entry?.approx_start_s);
    if (Number.isFinite(start) && currentTime >= start) {
      lastStartedId = entry.step_id;
    }
  }
  return lastStartedId ?? actionSteps[0]?.id ?? null;
}

function registerRewindForwardButtons(player) {
  if (!window.videojs || !player) return;

  const controlBar = player.getChild('controlBar');
  if (!controlBar) return;

  const Button = window.videojs.getComponent('Button');

  // Rewind 5s
  if (!controlBar.getChild('Rewind5Button')) {
    class Rewind5Button extends Button {
      constructor(playerInstance, options) {
        super(playerInstance, options);
        this.controlText('Rewind 5 seconds');
      }
      buildCSSClass() {
        return 'vjs-rewind-5 vjs-control vjs-button';
      }
      handleClick() {
        const t = Math.max(0, (player.currentTime() || 0) - 5);
        player.currentTime(t);
      }
    }
    if (!window.videojs.getComponent('Rewind5Button')) {
      window.videojs.registerComponent('Rewind5Button', Rewind5Button);
    }
    controlBar.addChild('Rewind5Button', {}, 1);
  }

  // Forward 5s
  if (!controlBar.getChild('Forward5Button')) {
    class Forward5Button extends Button {
      constructor(playerInstance, options) {
        super(playerInstance, options);
        this.controlText('Forward 5 seconds');
      }
      buildCSSClass() {
        return 'vjs-forward-5 vjs-control vjs-button';
      }
      handleClick() {
        const duration = player.duration() || Infinity;
        const t = Math.min(duration, (player.currentTime() || 0) + 5);
        player.currentTime(t);
      }
    }
    if (!window.videojs.getComponent('Forward5Button')) {
      window.videojs.registerComponent('Forward5Button', Forward5Button);
    }
    // Insert after rewind (position 2)
    controlBar.addChild('Forward5Button', {}, 2);
  }
}

const PlaySimulation = () => {
  const [searchParams] = useSearchParams();
  const protocolId = searchParams.get('id');

  const [protocol, setProtocol] = useState(null);
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stepActionSteps, setStepActionSteps] = useState([]);
  const [stepTimeline, setStepTimeline] = useState([]);
  const [activeInstructionId, setActiveInstructionId] = useState(null);
  const [referenceMaterials, setReferenceMaterials] = useState([]);
  const [refModalOpen, setRefModalOpen] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [refPages, setRefPages] = useState([]);
  const [currentRefPage, setCurrentRefPage] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [videoDimensions, setVideoDimensions] = useState({ width: null, height: null });
  const [awsStatus, setAwsStatus] = useState({ show: false, message: '', error: false });
  const [autoplayNotification, setAutoplayNotification] = useState({ show: false, message: '' });
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  const videoContainerRef = useRef(null);
  const playerRef = useRef(null);
  const autoPlayAttemptedRef = useRef(false);
  const currentStepRef = useRef(currentStep);
  const protocolIdRef = useRef(protocolId);
  const stepActionStepsRef = useRef([]);
  const stepTimelineRef = useRef([]);

  useEffect(() => { currentStepRef.current = currentStep; }, [currentStep]);
  useEffect(() => { protocolIdRef.current = protocolId; }, [protocolId]);
  useEffect(() => { stepActionStepsRef.current = stepActionSteps; }, [stepActionSteps]);
  useEffect(() => { stepTimelineRef.current = stepTimeline; }, [stepTimeline]);

  const totalSteps = steps.length;
  const stepNumber = currentStep + 1;
  const progressPercent = totalSteps > 0 ? Math.round(((currentStep + 1) / totalSteps) * 100) : 0;

  useEffect(() => {
    if (totalSteps > 0) {
      if (currentStep < 0) setCurrentStep(0);
      else if (currentStep >= totalSteps) setCurrentStep(totalSteps - 1);
    }
  }, [currentStep, totalSteps]);

  const fetchProtocol = useCallback(async () => {
    try {
      setLoading(true);
      const response = await API.get(`/qurator/protocols/${protocolId}`);
      if (response.data.success) {
        const data = response.data.protocol;
        setProtocol(data);
        const stepArray = data.orderedSteps && data.orderedSteps.length > 0
          ? data.orderedSteps.filter((s) => s && s.trim().length > 0)
          : (data.step_of_protocol ? data.step_of_protocol.split('`').filter((s) => s && s.trim().length > 0) : []);
        setSteps(stepArray);
        const stepParam = searchParams.get('step');
        if (stepParam) {
          const stepIndex = parseInt(stepParam, 10) - 1;
          if (stepIndex >= 0 && stepIndex < stepArray.length) setCurrentStep(stepIndex);
        }
      }
    } catch (error) {
      console.error('Error fetching protocol:', error);
    } finally {
      setLoading(false);
    }
  }, [protocolId, searchParams]);

  useEffect(() => {
    if (protocolId) {
      fetchProtocol();
    } else {
      setLoading(false);
    }
  }, [protocolId, fetchProtocol]);

  useEffect(() => {
    return () => {
      if (playerRef.current) {
        try {
          if (!playerRef.current.isDisposed()) playerRef.current.dispose();
        } catch (e) {
          console.error('Error disposing player on unmount:', e);
        }
        playerRef.current = null;
      }
    };
  }, []);

  const initializeVideoPlayer = useCallback(async () => {
    if (!videoContainerRef.current || !protocolId) return;

    try {
      if (playerRef.current) {
        try {
          if (!playerRef.current.isDisposed()) playerRef.current.dispose();
        } catch (e) {
          console.error('Error disposing player:', e);
        }
        playerRef.current = null;
      }

      videoContainerRef.current.innerHTML = '';
      autoPlayAttemptedRef.current = false;
      setAwsStatus({ show: true, message: '', error: false });

      const stepNum = currentStepRef.current + 1;
      const videoKey = `uploads/${protocolIdRef.current}/${protocolIdRef.current}_${stepNum}.mp4`;
      const response = await API.get(`/qurator/protocols/${protocolIdRef.current}/files/presigned-url?file_key=${encodeURIComponent(videoKey)}`);
      if (!response.data.success || !response.data.url) {
        setAwsStatus({ show: true, message: 'Video not found for this step', error: true });
        return;
      }

      if (!window.videojs) {
        setAwsStatus({ show: true, message: 'Video.js library not loaded', error: true });
        return;
      }

      const videoElement = document.createElement('video');
      videoElement.className = 'video-js vjs-big-play-centered';
      videoElement.setAttribute('playsinline', '');
      videoElement.setAttribute('controls', '');
      videoElement.style.width = '100%';
      videoElement.style.height = '100%';
      videoContainerRef.current.appendChild(videoElement);

      playerRef.current = window.videojs(videoElement, {
        controls: true,
        autoplay: false,
        preload: 'auto',
        fluid: false,
        fill: true,
        playsinline: true,
        loop: true,
        playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 2],
        userActions: { hotkeys: true },
        controlBar: {
          children: [
            'playToggle',
            'currentTimeDisplay',
            'progressControl',
            'durationDisplay',
            'volumePanel'
          ]
        }
      });
      const player = playerRef.current;
      player.addClass('sim-vjs-player');
      player.src({ src: response.data.url, type: 'video/mp4' });

      player.ready(() => {
        registerRewindForwardButtons(player);
        player.controlBar?.show();
      });

      let timeUpdateThrottle = null;
      let lastSavedTime = 0;
      let instructionSyncThrottle = null;
      player.on('loadedmetadata', () => {
        const video = player.tech({ IWillNotUseThisInPlugins: true }).el();
        if (video && video.videoWidth && video.videoHeight) {
          setVideoDimensions({ width: video.videoWidth, height: video.videoHeight });
        }
      });

      player.on('canplay', () => {
        setAwsStatus({ show: false, message: '', error: false });
        if (!autoPlayAttemptedRef.current) {
          autoPlayAttemptedRef.current = true;
          player.muted(true);
          const playPromise = player.play();
          if (playPromise !== undefined) {
            playPromise.then(() => {
              setAutoplayNotification({ show: true, message: 'Video is playing muted. Click to unmute.' });
              setTimeout(() => setAutoplayNotification({ show: false, message: '' }), 3000);
              player.one('click', () => player.muted(false));
            }).catch(() => player.muted(false));
          }
        }
      });

      player.on('error', () => {
        const error = player.error();
        setAwsStatus({ show: true, message: `Video error: ${error ? error.message : 'Unknown error'}`, error: true });
      });

      player.on('timeupdate', () => {
        if (timeUpdateThrottle) return;
        timeUpdateThrottle = setTimeout(() => {
          const currentTime = player.currentTime();
          if (Math.abs(currentTime - lastSavedTime) > 1) {
            localStorage.setItem(`videojs_progress_${protocolIdRef.current}_${currentStepRef.current + 1}`, currentTime);
            lastSavedTime = currentTime;
          }
          timeUpdateThrottle = null;
        }, 2000);

        if (!instructionSyncThrottle) {
          instructionSyncThrottle = setTimeout(() => {
            const currentTime = player.currentTime();
            const timeline = stepTimelineRef.current || [];
            const actionSteps = stepActionStepsRef.current || [];
            if (actionSteps.length > 0) {
              const activeId = getTimelineActiveStepId(currentTime, timeline, actionSteps);
              if (activeId != null) {
                setActiveInstructionId((prev) => (String(prev) === String(activeId) ? prev : activeId));
              }
            }
            instructionSyncThrottle = null;
          }, 300);
        }
      });

      const savedProgress = localStorage.getItem(`videojs_progress_${protocolIdRef.current}_${currentStepRef.current + 1}`);
      if (savedProgress && parseFloat(savedProgress) > 5) {
        player.one('loadedmetadata', () => player.currentTime(parseFloat(savedProgress)));
      }
    } catch (error) {
      if (error.response?.status === 403) setAwsStatus({ show: true, message: 'Access denied. Please check permissions.', error: true });
      else if (error.response?.status === 404) setAwsStatus({ show: true, message: 'Video not found for this step', error: true });
      else if (error.response?.status === 401) setAwsStatus({ show: true, message: 'Authentication required. Please login again.', error: true });
      else setAwsStatus({ show: true, message: 'Failed to load video. Please try again.', error: true });
    }
  }, [protocolId]);

  useEffect(() => {
    if (protocol && steps.length > 0) {
      const timer = setTimeout(() => initializeVideoPlayer(), 100);
      return () => clearTimeout(timer);
    }
  }, [currentStep, protocol, steps.length, initializeVideoPlayer]);

  const loadReferenceMaterials = useCallback(async () => {
    try {
      if (!protocolId || currentStep < 0) {
        setReferenceMaterials([]);
        return;
      }
      const response = await API.get(`/qurator/protocols/${protocolId}/reference-materials?step_number=${stepNumber}`);
      if (response.data?.success) {
        const materials = [];
        if (response.data.materials?.documents) {
          Object.values(response.data.materials.documents).forEach((doc) => {
            materials.push({ name: doc.name, type: 'document', key: doc.pages?.[0]?.key || '', pages: doc.pages || [], size: doc.totalSize });
          });
        }
        if (response.data.materials?.images) {
          response.data.materials.images.forEach((img) => materials.push({ name: img.name, type: 'image', key: img.key, size: img.size }));
        }
        if (response.data.materials?.videos) {
          response.data.materials.videos.forEach((vid) => materials.push({ name: vid.name, type: 'video', key: vid.key, size: vid.size }));
        }
        setReferenceMaterials(materials);
      } else setReferenceMaterials([]);
    } catch (_) {
      setReferenceMaterials([]);
    }
  }, [protocolId, currentStep, stepNumber]);

  const loadStepActionSteps = useCallback(async () => {
    try {
      if (!protocolId || currentStep < 0) {
        setStepActionSteps([]);
        setStepTimeline([]);
        setActiveInstructionId(null);
        return;
      }
      const response = await API.get(`/qurator/protocols/${protocolId}/human-validation?step_number=${stepNumber}`);
      if (response.data?.success) {
        const schema = response.data.schema && typeof response.data.schema === 'object' ? response.data.schema : {};
        const items = Array.isArray(schema.action_steps) ? schema.action_steps : [];
        const timeline = Array.isArray(schema.timeline) ? schema.timeline : [];
        setStepActionSteps(items);
        setStepTimeline(timeline);
        setActiveInstructionId(items[0]?.id ?? null);
      } else {
        setStepActionSteps([]);
        setStepTimeline([]);
        setActiveInstructionId(null);
      }
    } catch (_) {
      setStepActionSteps([]);
      setStepTimeline([]);
      setActiveInstructionId(null);
    }
  }, [protocolId, currentStep, stepNumber]);

  useEffect(() => {
    if (protocol && steps.length > 0) {
      loadReferenceMaterials();
      loadStepActionSteps();
    }
  }, [currentStep, protocol, steps.length, loadReferenceMaterials, loadStepActionSteps]);

  const cyclePlaybackSpeed = () => {
    const speeds = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const currentIdx = speeds.indexOf(playbackSpeed);
    const nextIdx = (currentIdx + 1) % speeds.length;
    const newSpeed = speeds[nextIdx];
    setPlaybackSpeed(newSpeed);
    if (playerRef.current && !playerRef.current.isDisposed()) {
      playerRef.current.playbackRate(newSpeed);
    }
  };

  const toggleFullscreen = () => {
    if (playerRef.current && !playerRef.current.isDisposed()) {
      if (playerRef.current.isFullscreen()) {
        playerRef.current.exitFullscreen();
      } else {
        playerRef.current.requestFullscreen();
      }
    }
  };

  const navigateStep = (newStep) => {
    if (newStep >= 0 && newStep < totalSteps) setCurrentStep(newStep);
  };

  const openReferenceMaterial = async (material) => {
    setSelectedMaterial(material);
    setCurrentRefPage(0);
    setZoomLevel(1);

    try {
      const response = await API.get(`/qurator/protocols/${protocolId}/files/presigned-url?file_key=${encodeURIComponent(material.key)}`);
      if (!(response.data.success && response.data.url)) return;
      if (material.type === 'document') {
        const pdf = await window.pdfjsLib.getDocument(response.data.url).promise;
        const pages = [];
        for (let i = 1; i <= pdf.numPages; i += 1) {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 2.0 });
          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const context = canvas.getContext('2d');
          await page.render({ canvasContext: context, viewport }).promise;
          pages.push(canvas.toDataURL());
        }
        setRefPages(pages);
      } else {
        setRefPages([response.data.url]);
      }
      setRefModalOpen(true);
    } catch (error) {
      console.error('Error loading material:', error);
    }
  };

  const closeRefModal = () => {
    setRefModalOpen(false);
    setSelectedMaterial(null);
    setRefPages([]);
    setCurrentRefPage(0);
    setZoomLevel(1);
  };

  const formatBytes = (value) => {
    const size = Number(value);
    if (!Number.isFinite(size) || size <= 0) return '';
    const units = ['B', 'KB', 'MB', 'GB'];
    const idx = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1);
    const n = size / Math.pow(1024, idx);
    return `${n.toFixed(idx === 0 ? 0 : 1)} ${units[idx]}`;
  };

  if (loading) {
    return (
      <QuratorLayout>
        <div className="play-simulation-loading">
          <div className="qrt-loading"></div>
          <p>Loading simulation...</p>
        </div>
      </QuratorLayout>
    );
  }

  if (!protocol) {
    return (
      <QuratorLayout>
        <div className="play-simulation-error">
          <i className="bi bi-exclamation-triangle"></i>
          <h3>Protocol Not Found</h3>
          <p>The requested protocol could not be loaded.</p>
        </div>
      </QuratorLayout>
    );
  }

  const activeInstruction = stepActionSteps.find((item) => String(item.id) === String(activeInstructionId))
    || stepActionSteps[0]
    || null;
  const experimentName = protocol.prt_title
    || protocol.draft_title
    || protocol.protocol_name
    || protocol.prt_name
    || protocol.experiment_name
    || `Experiment ${protocolId}`;
  const proTip = activeInstruction?.success_criteria?.[0]
    || activeInstruction?.step_context
    || 'Follow the step carefully and verify object placement before moving forward.';

  return (
    <QuratorLayout>
      <div className="play-simulation-container">
        <div className="simulation-top-bar slide-up">
          <div className="sim-title-wrap">
            <h1 className="protocol-title">{experimentName}</h1>
            <div className="sim-meta">
              <span><i className="bi bi-circle-fill"></i> Published</span>
              <span><i className="bi bi-calendar3"></i> {new Date(protocol.date).toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        <div className="sim-layout-grid">
          <div className="sim-main-column">
            <div className="protocol-card sim-video-card slide-up">
              <div className="card-body sim-video-body">
                <div className="video-wrapper sim-video-frame">
                  <div className="sim-video-step-badge">Step {stepNumber} of {totalSteps}</div>
                  <div className="sim-video-top-controls">
                    <button type="button" className="sim-vt-btn" onClick={cyclePlaybackSpeed} title="Playback Speed">
                      {playbackSpeed}x
                    </button>
                    <button type="button" className="sim-vt-btn" onClick={toggleFullscreen} title="Fullscreen">
                      <i className="bi bi-fullscreen"></i>
                    </button>
                  </div>
                  {awsStatus.show && (
                    <div className={`video-overlay ${awsStatus.error ? 'error' : ''} show`}>
                      {awsStatus.error ? (
                        <>
                          <i className="bi bi-cloud-slash"></i>
                          <span>{awsStatus.message}</span>
                        </>
                      ) : (
                        <div className="sim-video-loader"></div>
                      )}
                    </div>
                  )}
                  {autoplayNotification.show && (
                    <div className="autoplay-notification show">
                      <i className="bi bi-play-circle"></i>
                      <div>{autoplayNotification.message}</div>
                    </div>
                  )}
                  <div ref={videoContainerRef} className="video-container" />
                </div>
              </div>
            </div>

            <div className="sim-step-panel slide-up">
              <div className="sim-tabs">
                <button type="button" className="sim-tab sim-tab-active">Step Instructions</button>
              </div>
              <div className="sim-step-content">
                <div className="sim-step-list">
                  <div className="sim-step-list-header">
                    Action Steps {stepActionSteps.length > 0 ? `${stepActionSteps.findIndex(s => String(s.id) === String(activeInstructionId)) + 1} of ${stepActionSteps.length}` : `1 of 1`}
                  </div>
                  {stepActionSteps.length > 0 ? (
                    stepActionSteps.map((item, idx) => (
                      <div
                        key={`action-${item.id ?? idx}`}
                        className={`sim-action-item ${String(item.id) === String(activeInstruction?.id) ? 'is-active' : ''}`}
                      >
                        <span className="sim-step-list-index">{idx + 1}</span>
                        <span className="sim-action-label">{item.title || `Action ${idx + 1}`}</span>
                      </div>
                    ))
                  ) : (
                    <div className="sim-action-item is-active">
                      <span className="sim-step-list-index">1</span>
                      <span className="sim-action-label">{steps[currentStep] || 'Instruction'}</span>
                    </div>
                  )}
                </div>
                <div className="sim-step-details">
                  <div className="sim-step-count">Step {stepNumber} of {totalSteps}</div>
                  <h3 className="sim-main-step-title">{steps[currentStep] || ''}</h3>
                  <div className="sim-pro-tip">
                    <div className="sim-pro-tip-title"><i className="bi bi-lightbulb"></i> Pro Tip</div>
                    <p>{proTip}</p>
                  </div>
                </div>
              </div>
              <div className="navigation-buttons">
                {currentStep > 0 ? (
                  <button className="nav-btn secondary" onClick={() => navigateStep(currentStep - 1)}>
                    <i className="bi bi-arrow-left"></i>Previous Step
                  </button>
                ) : (
                  <span className="nav-btn disabled"><i className="bi bi-arrow-left"></i>Previous Step</span>
                )}
                {currentStep < totalSteps - 1 ? (
                  <button className="nav-btn primary" onClick={() => navigateStep(currentStep + 1)}>
                    Next Step<i className="bi bi-arrow-right"></i>
                  </button>
                ) : (
                  <span className="nav-btn disabled">Next Step<i className="bi bi-arrow-right"></i></span>
                )}
              </div>
            </div>
          </div>

          <aside className="sim-right-column">
            <div className="protocol-card sim-progress-card slide-up">
              <div className="card-header sim-flat-header">
                <h3>Your Progress</h3>
                <span>Step {stepNumber} of {totalSteps}</span>
              </div>
              <div className="card-body">
                <div className="sim-progress-ring" role="img" aria-label={`Progress ${progressPercent}%`}>
                  <svg viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r="52" className="sim-ring-bg"></circle>
                    <circle
                      cx="60"
                      cy="60"
                      r="52"
                      className="sim-ring-fill"
                      style={{ strokeDasharray: `${Math.PI * 2 * 52}`, strokeDashoffset: `${(1 - (progressPercent / 100)) * Math.PI * 2 * 52}` }}
                    ></circle>
                  </svg>
                  <div className="sim-progress-value">{progressPercent}%</div>
                </div>
                <p className="sim-progress-caption">Overall Progress</p>
                <p className="sim-progress-sub">{currentStep + 1} of {totalSteps} steps completed</p>
              </div>
            </div>

            <div className="protocol-card reference-section slide-up">
              <div className="card-header sim-flat-header"><h3>Reference Materials</h3></div>
              <div className="card-body">
                {referenceMaterials.length > 0 ? (
                  <div className="materials-list">
                    {referenceMaterials.map((material, index) => (
                      <div key={index} className="material-item">
                        <div className="material-icon">
                          <i className={`bi ${material.type === 'video' ? 'bi-play-circle' : material.type === 'image' ? 'bi-image' : 'bi-file-pdf'}`}></i>
                        </div>
                        <div className="material-info">
                          <div className="material-name">{material.name}</div>
                          <div className="material-type">{material.type.toUpperCase()} {material.size ? `• ${formatBytes(material.size)}` : ''}</div>
                        </div>
                        <button type="button" className="material-view-btn" onClick={() => openReferenceMaterial(material)} title="View" aria-label={`View ${material.name}`}>
                          <i className="bi bi-eye"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-materials">
                    <div className="no-materials-icon"><i className="bi bi-inbox"></i></div>
                    <h4>No reference materials</h4>
                    <p>No reference materials for this step</p>
                  </div>
                )}
              </div>
            </div>
          </aside>
        </div>

        {refModalOpen && (
          <div className="ref-modal-overlay" onClick={closeRefModal}>
            <div className="ref-modal" onClick={(e) => e.stopPropagation()}>
              <div className="ref-modal-header">
                <h3>{selectedMaterial?.name}</h3>
                <button className="close-btn" onClick={closeRefModal}>
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
              <div className="ref-modal-body">
                {selectedMaterial?.type === 'video'
                  ? <video src={refPages[0]} controls className="ref-video" />
                  : (
                    <div className="ref-viewer">
                      <img src={refPages[currentRefPage]} alt={`Page ${currentRefPage + 1}`} style={{ transform: `scale(${zoomLevel})` }} />
                    </div>
                    )}
              </div>
              {selectedMaterial?.type !== 'video' && refPages.length > 0 && (
                <div className="ref-modal-footer">
                  <div className="zoom-controls">
                    <button onClick={() => setZoomLevel(Math.max(0.5, zoomLevel - 0.25))}><i className="bi bi-zoom-out"></i></button>
                    <span>{Math.round(zoomLevel * 100)}%</span>
                    <button onClick={() => setZoomLevel(Math.min(3, zoomLevel + 0.25))}><i className="bi bi-zoom-in"></i></button>
                  </div>
                  {refPages.length > 1 && (
                    <div className="page-controls">
                      <button onClick={() => setCurrentRefPage(Math.max(0, currentRefPage - 1))} disabled={currentRefPage === 0}><i className="bi bi-chevron-left"></i></button>
                      <span>Page {currentRefPage + 1} of {refPages.length}</span>
                      <button onClick={() => setCurrentRefPage(Math.min(refPages.length - 1, currentRefPage + 1))} disabled={currentRefPage === refPages.length - 1}><i className="bi bi-chevron-right"></i></button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </QuratorLayout>
  );
};

export default PlaySimulation;
